// Extensions unified du rendu Markdown (MarkdownRenderer), sans JSX : testées sous Node
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { isNumericColumn } from './tableExport.js';

/** Texte brut d'un nœud HAST. */
export function hastText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.value;
  return (node.children ?? []).map(hastText).join('');
}

// Parcours des éléments HAST (ordre du document) : fn(nœud, parent, index)
function eachElement(node, fn) {
  (node.children ?? []).forEach((child, index) => {
    if (child.type !== 'element') return;
    fn(child, node, index);
    eachElement(child, fn);
  });
}

const childElements = (node, ...tags) => (node?.children ?? []).filter((c) => c.type === 'element' && tags.includes(c.tagName));

// ── Encadrés GFM : > [!NOTE] / [!TIP] / [!WARNING] / [!IMPORTANT] / [!CAUTION] ──
const CALLOUT_RE = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\][ \t]*(?:\n|$)/i;

export function remarkCallouts() {
  const walk = (node) => {
    if (node.type === 'blockquote') {
      const first = node.children?.[0];
      const text = first?.type === 'paragraph' ? first.children?.[0] : null;
      const match = text?.type === 'text' ? CALLOUT_RE.exec(text.value) : null;
      if (match) {
        const kind = match[1].toLowerCase();
        text.value = text.value.slice(match[0].length);
        if (!text.value) first.children.shift();
        if (first.children[0]?.type === 'break') first.children.shift();
        if (!first.children.length) node.children.shift();
        node.data = {
          ...node.data,
          hProperties: { ...node.data?.hProperties, dataCallout: kind },
        };
      }
    }
    node.children?.forEach(walk);
  };
  return walk;
}

// Formules entre $$…$$ seulement : « de 80 $ à 92 $ » reste un montant
export const REMARK_PLUGINS = [remarkGfm, [remarkMath, { singleDollarTextMath: false }], remarkCallouts];

/**
 * Colonnes numériques alignées à droite : toutes les cellules remplies du corps sont des
 * nombres (parseCellNumber ; vides et tirets ignorés, codes comme « 007 » exclus). Un
 * alignement GFM écrit (« :--- », « :---: ») est respecté. Classe md-num : chiffres
 * tabulaires, jamais coupés (« 1 234,5 FCFA » sur une ligne, le tableau défile).
 */
export function rehypeAlignNumbers() {
  return (tree) => {
    eachElement(tree, (table) => {
      if (table.tagName !== 'table') return;
      const rows = childElements(table, 'thead', 'tbody').flatMap((section) =>
        childElements(section, 'tr').map((tr) => ({ head: section.tagName === 'thead', cells: childElements(tr, 'th', 'td') })));
      const body = rows.filter((row) => !row.head);
      if (!body.length) return;
      const width = Math.max(...rows.map((row) => row.cells.length));
      for (let col = 0; col < width; col += 1) {
        if (!isNumericColumn(body.map((row) => hastText(row.cells[col])))) continue;
        const written = rows.some((row) => row.cells[col]?.properties?.align);
        for (const row of rows) {
          const cell = row.cells[col];
          if (!cell) continue;
          const className = [...[].concat(cell.properties?.className ?? []), 'md-num'];
          cell.properties = { ...cell.properties, className, ...(written ? {} : { align: 'right' }) };
        }
      }
    });
  };
}

/** Partie lisible d'une ancre : « Coût du parc (FCFA) » → « cout-du-parc-fcfa ». */
export function headingSlug(text) {
  const slug = String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 60)
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
}

/**
 * Identifiants des titres h2 / h3, uniques dans la page : préfixe du message, texte du
 * titre, suffixe -2, -3… pour un titre répété (ou déjà pris, notes de bas de page comprises).
 * Le titre généré des notes (« footnote-label », sans préfixe) reçoit aussi le préfixe,
 * renvois aria-describedby compris.
 */
export function rehypeHeadingIds({ prefix = 'md' } = {}) {
  const base = String(prefix).replace(/[^\w-]+/g, '-');
  const footnoteLabel = `${base}-footnote-label`;
  return (tree) => {
    const used = new Set();
    eachElement(tree, (node) => {
      if (node.properties?.id === 'footnote-label') node.properties = { ...node.properties, id: footnoteLabel };
      const described = node.properties?.ariaDescribedBy;
      if (Array.isArray(described) && described.includes('footnote-label')) {
        node.properties = { ...node.properties, ariaDescribedBy: described.map((v) => (v === 'footnote-label' ? footnoteLabel : v)) };
      }
      if (node.properties?.id) used.add(String(node.properties.id));
    });
    eachElement(tree, (node) => {
      if ((node.tagName !== 'h2' && node.tagName !== 'h3') || node.properties?.id || !node.position) return;
      const wanted = `${base}-${headingSlug(hastText(node))}`;
      let id = wanted;
      for (let n = 2; used.has(id); n += 1) id = `${wanted}-${n}`;
      used.add(id);
      node.properties = { ...node.properties, id };
    });
  };
}

/**
 * Lien vers l'ancre d'un titre, limité à la page (« #id ») : l'adresse n'identifie pas la
 * conversation, et l'assistant s'ouvre dans une iframe de la plateforme ; une adresse
 * complète, partagée, ouvrirait un assistant vide.
 */
export const headingLink = (id) => `#${id}`;

// Renvoi de note « [^n] » resté en texte : sa définition n'est pas (encore) dans la source
const FOOTNOTE_CALL_RE = /\[\^([^\]\s[]+)\]/g;
// Pas de renvoi provisoire dans le code ni dans un lien
const NO_FOOTNOTE_TAGS = new Set(['code', 'pre', 'a', 'kbd', 'samp']);

/**
 * Streaming : un renvoi de note « [^n] » dont la définition n'est pas encore arrivée (le
 * modèle l'écrit en dernière ligne) s'affiche déjà comme l'exposant final, sans lien.
 * À l'arrivée de la définition, remark-gfm le remplace par le vrai renvoi, de même taille :
 * la ligne ne grandit pas et le texte ne bouge pas à la fin du flux. Numéros dans l'ordre
 * d'apparition, après ceux des renvois déjà résolus (ceux de remark-gfm à la fin du flux).
 */
export function rehypeFootnotePending() {
  return (tree) => {
    let resolved = 0;
    eachElement(tree, (node) => {
      if (node.tagName !== 'a' || !('dataFootnoteRef' in (node.properties ?? {}))) return;
      const n = Number.parseInt(hastText(node), 10);
      if (Number.isFinite(n)) resolved = Math.max(resolved, n);
    });
    const pending = new Map();
    const numberOf = (label) => {
      const key = label.toLowerCase();
      if (!pending.has(key)) pending.set(key, resolved + pending.size + 1);
      return pending.get(key);
    };
    const split = (value) => {
      const out = [];
      let last = 0;
      for (const match of value.matchAll(FOOTNOTE_CALL_RE)) {
        if (match.index > last) out.push({ type: 'text', value: value.slice(last, match.index) });
        out.push({
          type: 'element',
          tagName: 'sup',
          properties: {},
          children: [{
            type: 'element',
            tagName: 'span',
            properties: { className: ['md-footnote-pending'] },
            children: [{ type: 'text', value: String(numberOf(match[1])) }],
          }],
        });
        last = match.index + match[0].length;
      }
      if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
      return out;
    };
    const walk = (node) => {
      if (!node.children || (node.type === 'element' && NO_FOOTNOTE_TAGS.has(node.tagName))) return;
      node.children = node.children.flatMap((child) => {
        if (child.type === 'text' && child.value.includes('[^')) return split(child.value);
        walk(child);
        return [child];
      });
    };
    walk(tree);
  };
}

export const MATH_PENDING_TEXT = 'Formule en cours…';

/**
 * Streaming : la formule $$ encore ouverte (fermée provisoirement par streamingMarkdown,
 * donc la dernière du texte) est remplacée par un espace réservé neutre, avant KaTeX.
 */
export function rehypeMathPending() {
  return (tree) => {
    let last = null;
    eachElement(tree, (node, parent, index) => {
      const code = childElements(node, 'code')[0];
      if (node.tagName === 'pre' && [].concat(code?.properties?.className ?? []).includes('math-display')) last = { parent, index };
    });
    if (!last) return;
    last.parent.children[last.index] = {
      type: 'element',
      tagName: 'div',
      properties: { className: ['md-math-pending'] },
      children: [{ type: 'text', value: MATH_PENDING_TEXT }],
    };
  };
}
